// Schedule template color tokens + open-shift fill modes, frozen by the Wave 6A DB contract
// (2026-08-08) and shipped in migration 20260808120000_schedule_color_open_fill.
//
// The database stores a TOKEN (`public.shift_templates.color_token`, closed check constraint);
// this file is the only place a hex exists. Clients never send a hex, and never echo an
// unvalidated server string into a style attribute - that is the injection the closed
// allowlist exists to prevent. One source for console (6B), guard web (6C), and mobile (6D).

/** The 12 frozen tokens, in the picker's order (6A contract, Color allowlist). */
export const COLOR_TOKENS = [
  "slate",
  "blue",
  "indigo",
  "teal",
  "green",
  "amber",
  "orange",
  "rose",
  "red",
  "violet",
  "cyan",
  "stone",
] as const;

export type ColorToken = (typeof COLOR_TOKENS)[number];

/** Column default in the migration, and the fallback for anything unrecognized. */
export const COLOR_TOKEN_DEFAULT: ColorToken = "slate";

/** Token -> hex. Suggested hexes from the 6A contract (UI only; never stored). */
export const COLOR_TOKEN_HEX: Readonly<Record<ColorToken, string>> = {
  slate: "#64748b",
  blue: "#3b82f6",
  indigo: "#6366f1",
  teal: "#14b8a6",
  green: "#22c55e",
  amber: "#f59e0b",
  orange: "#f97316",
  rose: "#f43f5e",
  red: "#ef4444",
  violet: "#8b5cf6",
  cyan: "#06b6d4",
  stone: "#78716c",
};

/** Human label beside every swatch - WCAG 1.4.1: color never carries meaning alone. */
export const COLOR_TOKEN_COPY: Readonly<Record<ColorToken, string>> = {
  slate: "Slate",
  blue: "Blue",
  indigo: "Indigo",
  teal: "Teal",
  green: "Green",
  amber: "Amber",
  orange: "Orange",
  rose: "Rose",
  red: "Red",
  violet: "Violet",
  cyan: "Cyan",
  stone: "Stone",
};

/** Unknown/absent -> slate. Keep the row; never render the raw string as a color. */
export function asColorToken(value: unknown): ColorToken {
  return typeof value === "string" && (COLOR_TOKENS as readonly string[]).includes(value)
    ? (value as ColorToken)
    : COLOR_TOKEN_DEFAULT;
}

/** True only for one of the 12 frozen tokens - used to refuse a bad token before the RPC. */
export function isColorToken(value: unknown): value is ColorToken {
  return typeof value === "string" && (COLOR_TOKENS as readonly string[]).includes(value);
}

/** `public.open_fill_mode` enum values, frozen. */
export const OPEN_FILL_MODES = ["instant_claim", "request_approval"] as const;

export type OpenFillMode = (typeof OPEN_FILL_MODES)[number];

/** Column default; also what a template-less slot behaves as (contract + view coalesce). */
export const OPEN_FILL_MODE_DEFAULT: OpenFillMode = "instant_claim";

/** Absent/unknown mode means instant claim - the contract's backward-compatible default. */
export function asOpenFillMode(value: unknown): OpenFillMode {
  return value === "request_approval" ? "request_approval" : OPEN_FILL_MODE_DEFAULT;
}

export const OPEN_FILL_MODE_COPY: Readonly<Record<OpenFillMode, string>> = {
  instant_claim: "Instant claim",
  request_approval: "Request approval",
};

/** The guard-facing consequence, stated at the point of decision. */
export const OPEN_FILL_MODE_HELP: Readonly<Record<OpenFillMode, string>> = {
  instant_claim: "Guards take an open seat immediately.",
  request_approval: "Guards ask; a supervisor approves before the seat is filled.",
};

/** `public.open_shift_request_status` enum values, frozen. */
export const OPEN_SHIFT_REQUEST_STATUSES = [
  "pending",
  "approved",
  "rejected",
  "cancelled",
] as const;

export type OpenShiftRequestStatus = (typeof OPEN_SHIFT_REQUEST_STATUSES)[number];

export function asOpenShiftRequestStatus(value: unknown): OpenShiftRequestStatus | null {
  return typeof value === "string" &&
    (OPEN_SHIFT_REQUEST_STATUSES as readonly string[]).includes(value)
    ? (value as OpenShiftRequestStatus)
    : null;
}

// --- Frozen RPC names (6A contract, Client constants) ---------------------------------------
// Invent nothing here: every name below is in the shipped migration.

/** Guard / eligible member write paths (6C + 6D own the UI; named here so nobody re-spells them). */
export const REQUEST_OPEN_SHIFT_SLOT_RPC = "request_open_shift_slot";
export const CANCEL_OPEN_SHIFT_REQUEST_RPC = "cancel_open_shift_request";
export const LIST_MY_OPEN_SHIFT_REQUESTS_RPC = "list_my_open_shift_requests";
/** Existing instant-claim path - reused, never renamed to bid/apply. */
export const CLAIM_OPEN_SHIFT_SLOT_RPC = "claim_open_shift_slot";

/** Desk paths, all `schedule.manage`-gated server-side. */
export const LIST_ACCOUNT_OPEN_SHIFT_REQUESTS_RPC = "list_account_open_shift_requests";
export const APPROVE_OPEN_SHIFT_REQUEST_RPC = "approve_open_shift_request";
export const REJECT_OPEN_SHIFT_REQUEST_RPC = "reject_open_shift_request";

/** Template mutations, extended in 6A with the two optional params below. */
export const CREATE_SHIFT_TEMPLATE_RPC = "create_shift_template";
export const UPDATE_SHIFT_TEMPLATE_RPC = "update_shift_template";
export const SHIFT_TEMPLATE_COLOR_TOKEN_PARAM = "p_color_token";
export const SHIFT_TEMPLATE_OPEN_FILL_MODE_PARAM = "p_open_fill_mode";

/**
 * Template lifecycle (migration 20260815120000), both `schedule.manage`-gated.
 *
 * `discard_shift_template` HARD-DELETES a never-published draft that nothing else
 * references; `retire_shift_template` soft-retires a published template and leaves
 * every slot on the calendar. There is no unretire RPC - do not invent one here.
 */
export const DISCARD_SHIFT_TEMPLATE_RPC = "discard_shift_template";
export const RETIRE_SHIFT_TEMPLATE_RPC = "retire_shift_template";

/** Permission gating every desk path in this surface. No new permission key in Wave 6. */
export const SCHEDULE_MANAGE_PERMISSION = "schedule.manage";

/** Server-side cap on note and rejection-reason length (both table check constraints). */
export const OPEN_SHIFT_REQUEST_TEXT_MAX = 500;
